import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  FormControl,
  FormLabel,
  HStack,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
  VStack,
} from '@chakra-ui/react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FiUnlock } from 'react-icons/fi'
import { DropdownSelect } from '@/components/common/DropdownSelect'
import { RefreshIconButton } from '@/components/common/RefreshIconButton'
import { useAuth } from '@/contexts/AuthContext'
import { useNetworkEnvironment } from '@/hooks/useNetworkEnvironment'
import { useToast } from '@/hooks/useToast'
import {
  cooldownService,
  type CooldownRecordRow,
} from '@/services/admin/cooldownService'

const formatDateTime = (value?: string | null) => {
  if (!value) return '-'
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString()
}

const toBool = (value: unknown): boolean =>
  value === true || value === 1 || value === '1'

const statusColor = (status?: string) => {
  switch (status) {
    case 'active':
      return 'green'
    case 'overridden':
      return 'purple'
    case 'cancelled':
      return 'orange'
    case 'expired':
      return 'gray'
    default:
      return 'gray'
  }
}

const getRecordUserLabel = (record?: CooldownRecordRow | null) =>
  record?.user_email || record?.identifier || '-'

const getRecordOperationLabel = (record?: CooldownRecordRow | null) =>
  record?.operation_display_name || record?.operation_type || '-'

export function CooldownRecordsMonitor({ isActive }: { isActive: boolean }) {
  const { t } = useTranslation()
  const toast = useToast()
  const queryClient = useQueryClient()
  const { currentEnv } = useNetworkEnvironment()
  const { checkPermission } = useAuth()
  const canOverride = checkPermission('cooldown:override')
  const modal = useDisclosure()
  const [selectedRecord, setSelectedRecord] =
    useState<CooldownRecordRow | null>(null)
  const [overrideReason, setOverrideReason] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [filters, setFilters] = useState({
    operationType: '',
    identifier: '',
    status: '',
  })

  const queryKey = ['cooldown', 'records', currentEnv, page, pageSize, filters]
  const recordsQuery = useQuery({
    queryKey,
    enabled: isActive,
    queryFn: () =>
      cooldownService.getRecords(currentEnv, {
        page,
        limit: pageSize,
        operationType: filters.operationType,
        identifier: filters.identifier,
        status: filters.status,
      }),
  })
  const statsQuery = useQuery({
    queryKey: ['cooldown', 'stats', currentEnv, filters.operationType],
    enabled: isActive,
    queryFn: () =>
      cooldownService.getStats(currentEnv, {
        operationType: filters.operationType || undefined,
        hours: 24,
      }),
  })
  const operationTypesQuery = useQuery({
    queryKey: ['cooldown', 'operationTypes', currentEnv],
    enabled: isActive,
    queryFn: () => cooldownService.getOperationTypes(currentEnv),
  })

  const overrideMutation = useMutation({
    mutationFn: () => {
      if (!selectedRecord) {
        throw new Error(t('monitoring.cooldown.override.noSelection'))
      }
      const payload = {
        operationType: selectedRecord.operation_type,
        identifier: selectedRecord.identifier,
        context: selectedRecord.context,
        reason: overrideReason,
      }
      if (selectedRecord.source !== 'kv' && selectedRecord.id) {
        return cooldownService.overrideRecord(
          currentEnv,
          selectedRecord.id,
          payload
        )
      }
      return cooldownService.override(currentEnv, payload)
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['cooldown', 'records', currentEnv],
      })
      await queryClient.invalidateQueries({
        queryKey: ['cooldown', 'stats', currentEnv],
      })
      toast.success({
        title: t('monitoring.cooldown.override.success'),
      })
      modal.onClose()
      setSelectedRecord(null)
      setOverrideReason('')
    },
    onError: error => {
      toast.error({
        title: t('monitoring.cooldown.override.failed'),
        description: error instanceof Error ? error.message : String(error),
      })
    },
  })

  const rows = useMemo(() => {
    const d1Rows = recordsQuery.data?.records ?? []
    const kvRows = recordsQuery.data?.kvRecords ?? []
    return [...kvRows, ...d1Rows]
  }, [recordsQuery.data])
  const overview = statsQuery.data?.overview
  const operationTypeOptions = useMemo(
    () => [
      { value: '', label: t('common.all') },
      ...(operationTypesQuery.data?.dbTypes ?? []).map(row => ({
        value: row.operation_type,
        label: row.display_name || row.operation_type,
      })),
    ],
    [operationTypesQuery.data?.dbTypes, t]
  )

  const pageSizeOptions = [10, 20, 50, 100].map(size => ({
    value: String(size),
    label: String(size),
  }))
  const totalRecords = recordsQuery.data?.total ?? rows.length
  const totalPages =
    recordsQuery.data?.totalPages ??
    Math.max(1, Math.ceil(totalRecords / pageSize))

  const openOverrideModal = (row: CooldownRecordRow) => {
    setSelectedRecord(row)
    setOverrideReason('')
    modal.onOpen()
  }

  return (
    <VStack align="stretch" spacing={4}>
      <SimpleGrid columns={{ base: 2, md: 3, xl: 5 }} spacing={3}>
        {[
          {
            label: t('monitoring.cooldown.stats.total'),
            value: overview?.totalRecords ?? 0,
          },
          {
            label: t('monitoring.cooldown.stats.active'),
            value: overview?.activeRecords ?? 0,
          },
          {
            label: t('monitoring.cooldown.stats.overridden'),
            value: overview?.overriddenRecords ?? 0,
          },
          {
            label: t('monitoring.cooldown.stats.kvActive'),
            value: statsQuery.data?.kvActiveRecords ?? 0,
          },
        ].map(metric => (
          <Card key={metric.label} borderRadius="md" variant="outline">
            <CardBody py={2} px={3}>
              <HStack
                justify="space-between"
                align="center"
                minH="28px"
                spacing={3}
              >
                <Text
                  fontSize="xs"
                  fontWeight="semibold"
                  color="gray.600"
                  noOfLines={1}
                >
                  {metric.label}
                </Text>
                <Text fontSize="lg" fontWeight="bold" lineHeight="1">
                  {metric.value}
                </Text>
              </HStack>
            </CardBody>
          </Card>
        ))}
      </SimpleGrid>

      <HStack justify="space-between" align="center" flexWrap="wrap" gap={3}>
        <Text fontWeight="semibold">{t('monitoring.cooldown.title')}</Text>
        <RefreshIconButton
          label={t('common.refresh')}
          onClick={() => {
            void recordsQuery.refetch()
            void statsQuery.refetch()
            void operationTypesQuery.refetch()
          }}
          isLoading={
            recordsQuery.isFetching ||
            statsQuery.isFetching ||
            operationTypesQuery.isFetching
          }
        />
      </HStack>

      <HStack align="end" spacing={3} flexWrap="wrap">
        <FormControl w={{ base: 'full', md: '280px' }}>
          <FormLabel as="div" fontSize="xs">
            {t('monitoring.cooldown.columns.operationType')}
          </FormLabel>
          <DropdownSelect
            size="sm"
            value={filters.operationType}
            options={operationTypeOptions}
            ariaLabel={t('monitoring.cooldown.columns.operationType')}
            onChange={value => {
              setPage(1)
              setFilters(prev => ({ ...prev, operationType: value }))
            }}
          />
        </FormControl>
        <FormControl w={{ base: 'full', md: '260px' }}>
          <FormLabel fontSize="xs">
            {t('monitoring.cooldown.columns.user')}
          </FormLabel>
          <Input
            size="sm"
            value={filters.identifier}
            onChange={event => {
              setPage(1)
              setFilters(prev => ({ ...prev, identifier: event.target.value }))
            }}
          />
        </FormControl>
        <FormControl w={{ base: 'full', md: '180px' }}>
          <FormLabel as="div" fontSize="xs">
            {t('monitoring.cooldown.columns.status')}
          </FormLabel>
          <DropdownSelect
            size="sm"
            value={filters.status}
            options={[
              { value: '', label: t('common.all') },
              {
                value: 'active',
                label: t('monitoring.cooldown.statuses.active'),
              },
              {
                value: 'expired',
                label: t('monitoring.cooldown.statuses.expired'),
              },
              {
                value: 'overridden',
                label: t('monitoring.cooldown.statuses.overridden'),
              },
              {
                value: 'cancelled',
                label: t('monitoring.cooldown.statuses.cancelled'),
              },
            ]}
            onChange={value => {
              setPage(1)
              setFilters(prev => ({ ...prev, status: value }))
            }}
          />
        </FormControl>
      </HStack>

      {recordsQuery.error ? (
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          {recordsQuery.error instanceof Error
            ? recordsQuery.error.message
            : t('common.unknownError')}
        </Alert>
      ) : recordsQuery.isLoading ? (
        <HStack justify="center" py={10}>
          <Spinner size="md" />
        </HStack>
      ) : rows.length === 0 ? (
        <Alert status="info" borderRadius="md">
          <AlertIcon />
          {t('monitoring.cooldown.empty')}
        </Alert>
      ) : (
        <TableContainer borderWidth="1px" borderRadius="md">
          <Table size="sm">
            <Thead>
              <Tr>
                <Th>{t('monitoring.cooldown.columns.source')}</Th>
                <Th>{t('monitoring.cooldown.columns.operationType')}</Th>
                <Th>{t('monitoring.cooldown.columns.user')}</Th>
                <Th>{t('monitoring.cooldown.columns.context')}</Th>
                <Th>{t('monitoring.cooldown.columns.status')}</Th>
                <Th>{t('monitoring.cooldown.columns.expiresAt')}</Th>
                <Th>{t('monitoring.cooldown.columns.reason')}</Th>
                <Th textAlign="right">{t('common.actions')}</Th>
              </Tr>
            </Thead>
            <Tbody>
              {rows.map((row, index) => {
                const status = String(row.effective_status ?? row.status ?? '')
                const active =
                  row.effective_status === 'active' || row.status === 'active'
                const overrideAllowed = toBool(row.admin_override_allowed)
                const canOpenOverride = canOverride && active && overrideAllowed

                return (
                  <Tr key={row.id ?? row.key ?? index}>
                    <Td>
                      <Badge
                        colorScheme={row.source === 'kv' ? 'purple' : 'blue'}
                        variant="subtle"
                      >
                        {String(row.source ?? 'd1')}
                      </Badge>
                    </Td>
                    <Td>
                      <VStack align="start" spacing={1}>
                        <Text
                          fontSize="sm"
                          wordBreak="break-word"
                          overflowWrap="anywhere"
                        >
                          {getRecordOperationLabel(row)}
                        </Text>
                        {row.operation_type_join_status &&
                        row.operation_type_join_status !== 'ok' ? (
                          <Badge colorScheme="orange" variant="subtle">
                            {t(
                              `monitoring.cooldown.joinStatuses.${row.operation_type_join_status}`
                            )}
                          </Badge>
                        ) : null}
                      </VStack>
                    </Td>
                    <Td>
                      <Text
                        fontSize="sm"
                        wordBreak="break-word"
                        overflowWrap="anywhere"
                      >
                        {getRecordUserLabel(row)}
                      </Text>
                    </Td>
                    <Td>
                      <Text
                        fontFamily="mono"
                        fontSize="xs"
                        whiteSpace="normal"
                        wordBreak="break-word"
                        overflowWrap="anywhere"
                      >
                        {row.context ? String(row.context) : '-'}
                      </Text>
                    </Td>
                    <Td>
                      <Badge colorScheme={statusColor(status)} variant="subtle">
                        {status
                          ? t(`monitoring.cooldown.statuses.${status}`, {
                              defaultValue: status,
                            })
                          : '-'}
                      </Badge>
                    </Td>
                    <Td>
                      <Text fontSize="xs" lineHeight="1.25" whiteSpace="normal">
                        {formatDateTime(row.expires_at)}
                      </Text>
                    </Td>
                    <Td>
                      <Text
                        fontSize="sm"
                        whiteSpace="normal"
                        wordBreak="break-word"
                        overflowWrap="anywhere"
                      >
                        {row.reason ? String(row.reason) : '-'}
                      </Text>
                    </Td>
                    <Td textAlign="right">
                      <Tooltip label={t('monitoring.cooldown.override.action')}>
                        <IconButton
                          aria-label={t('monitoring.cooldown.override.action')}
                          icon={<FiUnlock />}
                          size="xs"
                          variant="ghost"
                          isDisabled={!canOpenOverride}
                          onClick={() => openOverrideModal(row)}
                        />
                      </Tooltip>
                    </Td>
                  </Tr>
                )
              })}
            </Tbody>
          </Table>
        </TableContainer>
      )}

      {totalRecords > 0 && (
        <HStack justify="space-between" align="center" flexWrap="wrap" gap={3}>
          <HStack spacing={4} flexWrap="wrap">
            <Text fontSize="sm" color="gray.500">
              {t('common.pagination.pageOf', {
                current: page,
                total: totalPages,
                defaultValue: `Page ${page} of ${totalPages}`,
              })}
            </Text>
            <HStack spacing={2}>
              <Text fontSize="sm" color="gray.500" whiteSpace="nowrap">
                {t('common.pagination.pageSize')}
              </Text>
              <Box w="84px">
                <DropdownSelect
                  size="xs"
                  value={String(pageSize)}
                  options={pageSizeOptions}
                  onChange={value => {
                    setPageSize(Number(value))
                    setPage(1)
                  }}
                />
              </Box>
            </HStack>
          </HStack>
          <HStack>
            <Button
              size="sm"
              onClick={() => setPage(value => value - 1)}
              isDisabled={page <= 1}
            >
              {t('common.previous')}
            </Button>
            <Button
              size="sm"
              onClick={() => setPage(value => value + 1)}
              isDisabled={page >= totalPages}
            >
              {t('common.next')}
            </Button>
          </HStack>
        </HStack>
      )}

      <Modal isOpen={modal.isOpen} onClose={modal.onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('monitoring.cooldown.override.title')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={4}>
              <Box>
                <Text fontSize="xs" color="gray.500">
                  {t('monitoring.cooldown.columns.operationType')}
                </Text>
                <Text>{getRecordOperationLabel(selectedRecord)}</Text>
              </Box>
              <Box>
                <Text fontSize="xs" color="gray.500">
                  {t('monitoring.cooldown.columns.user')}
                </Text>
                <Text wordBreak="break-all">
                  {getRecordUserLabel(selectedRecord)}
                </Text>
              </Box>
              <FormControl isRequired>
                <FormLabel>
                  {t('monitoring.cooldown.override.reason')}
                </FormLabel>
                <Textarea
                  value={overrideReason}
                  onChange={event => setOverrideReason(event.target.value)}
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <HStack>
              <Button variant="ghost" onClick={modal.onClose}>
                {t('common.cancel')}
              </Button>
              <Button
                colorScheme="red"
                onClick={() => overrideMutation.mutate()}
                isDisabled={overrideReason.trim().length === 0}
                isLoading={overrideMutation.isPending}
              >
                {t('monitoring.cooldown.override.action')}
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </VStack>
  )
}
